'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

interface ConversionTrackerProps {
    onEngagementMilestone?: (milestone: string, data: EngagementData) => void;
    onConversionAction?: (action: string, data: ConversionData) => void;
    onSectionChange?: (section: string, timeSpent: number) => void;
}

interface EngagementData {
    scrollDepth: number;
    timeOnPage: number;
    sectionsVisited: string[];
    interactionCount: number;
}

interface ConversionData {
    action: string;
    timestamp: number;
    context: string;
}

interface SectionTiming {
    [key: string]: number;
}

const TRACKED_SECTIONS = ['hero', 'how-it-works', 'demo', 'about', 'testimonials', 'start', 'faq'] as const;

const ConversionTracker = ({
    onEngagementMilestone,
    onConversionAction,
    onSectionChange,
}: ConversionTrackerProps) => {
    const [scrollDepth, setScrollDepth] = useState<number>(0);
    const [timeOnPage, setTimeOnPage] = useState<number>(0);
    const [sectionsVisited, setSectionsVisited] = useState<Set<string>>(new Set());
    const [interactionCount, setInteractionCount] = useState<number>(0);
    const [currentSection, setCurrentSection] = useState<string>('hero');
    const [sectionTimings, setSectionTimings] = useState<SectionTiming>({});
    const [exitIntentShown, setExitIntentShown] = useState<boolean>(false);
    const reportedMilestones = useRef(new Set<string>());

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeOnPage((prev) => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const depth = Math.min((scrollTop / docHeight) * 100, 100);
            setScrollDepth(depth);

            for (const section of TRACKED_SECTIONS) {
                const element = document.getElementById(section);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= window.innerHeight * 0.5 && rect.bottom >= window.innerHeight * 0.5) {
                        if (section !== currentSection) {
                            const timeSpent = sectionTimings[currentSection] || 0;
                            onSectionChange?.(currentSection, timeSpent);
                            setCurrentSection(section);
                            setSectionTimings((prev) =>
                                prev[section] === undefined ? { ...prev, [section]: 0 } : prev
                            );
                        }
                        setSectionsVisited((prev) => new Set(prev).add(section));
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [currentSection, sectionTimings, onSectionChange]);

    useEffect(() => {
        const timer = setInterval(() => {
            setSectionTimings((prev) => ({
                ...prev,
                [currentSection]: (prev[currentSection] || 0) + 1,
            }));
        }, 1000);

        return () => clearInterval(timer);
    }, [currentSection]);

    useEffect(() => {
        const eligible = [
            scrollDepth >= 25 && sectionsVisited.size >= 2 ? 'engaged_visitor' : null,
            scrollDepth >= 50 && timeOnPage >= 30 ? 'interested_visitor' : null,
            scrollDepth >= 75 && sectionsVisited.size >= 4 ? 'highly_engaged' : null,
        ].filter((milestone): milestone is string => milestone !== null);

        for (const milestone of eligible) {
            if (reportedMilestones.current.has(milestone)) continue;
            reportedMilestones.current.add(milestone);
            onEngagementMilestone?.(milestone, {
                scrollDepth,
                timeOnPage,
                sectionsVisited: Array.from(sectionsVisited),
                interactionCount,
            });
        }
    }, [scrollDepth, timeOnPage, sectionsVisited, interactionCount, onEngagementMilestone]);

    const handleExitIntent = useCallback(
        (e: MouseEvent) => {
            if (e.clientY <= 0 && !exitIntentShown && timeOnPage >= 10) {
                setExitIntentShown(true);
                onConversionAction?.('exit_intent_triggered', {
                    action: 'exit_intent',
                    timestamp: Date.now(),
                    context: `Scroll: ${scrollDepth.toFixed(0)}%, Time: ${timeOnPage}s`,
                });
            }
        },
        [exitIntentShown, timeOnPage, scrollDepth, onConversionAction]
    );

    useEffect(() => {
        document.addEventListener('mouseleave', handleExitIntent);
        return () => document.removeEventListener('mouseleave', handleExitIntent);
    }, [handleExitIntent]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (
                target.tagName === 'BUTTON' ||
                target.tagName === 'A' || target.closest('button') ||
                target.closest('a')
            ) {
                setInteractionCount((prev) => prev + 1);
                onConversionAction?.('interaction', {
                    action: 'click',
                    timestamp: Date.now(),
                    context: target.textContent || 'unknown',
                });
            }
        };

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [onConversionAction]);

    return null;
};

export default ConversionTracker;
